import { useRef, useState } from "react";
import { Upload, Loader2, X, AlertCircle } from "lucide-react";
import { useAppServices } from "../../hook/services";

interface Props {
  value: string;
  onChange: (url: string) => void;
  // Constrain the file picker; GHL caps images at 25MB.
  accept?: string;
}

const readAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(new Error("read failed"));
    r.onload = () => {
      const result = String(r.result || "");
      resolve(result.split(",")[1] || ""); // strip the data: prefix
    };
    r.readAsDataURL(file);
  });

// Upload-only image field: the file is pushed to the account's GHL media
// library and the returned hosted URL is stored in `value`.
const ImageInput = ({ value, onChange, accept = "image/*" }: Props) => {
  const services = useAppServices();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file?: File) => {
    if (!file) return;
    setError(null);
    if (file.size > 25 * 1024 * 1024) {
      setError("Image must be under 25MB.");
      return;
    }
    setUploading(true);
    try {
      const data = await readAsBase64(file);
      const { response, error: err } = await services.ghl.upload_image({
        payload: { filename: file.name, mimetype: file.type, data },
      });
      if (err || !response?.url) {
        setError(err?.message || "Upload failed. Check your CRM connection.");
      } else {
        onChange(response.url);
      }
    } catch {
      setError("Couldn't read that file.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-2">
      <input
        ref={fileRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        className="w-full flex items-center justify-center gap-2 h-10 bg-white border border-dashed border-ink-300 rounded-lg text-sm text-ink-600 hover:border-brand-400 hover:text-brand-700 disabled:opacity-60"
      >
        {uploading ? (
          <>
            <Loader2 size={14} className="animate-spin" /> Uploading…
          </>
        ) : (
          <>
            <Upload size={14} /> {value ? "Replace image" : "Upload an image"}
          </>
        )}
      </button>

      {error && (
        <p className="text-[11px] text-rose-600 flex items-center gap-1">
          <AlertCircle size={11} className="shrink-0" />
          {error}
        </p>
      )}

      {value && (
        <div className="flex items-center gap-2 rounded-lg border border-ink-200 bg-ink-50/40 p-2">
          <img
            src={value}
            alt="preview"
            className="w-10 h-10 rounded object-cover bg-ink-100"
            onError={(e) => {
              e.currentTarget.style.visibility = "hidden";
            }}
          />
          <span className="flex-1 min-w-0 text-[11px] text-ink-500 font-mono truncate">
            {value}
          </span>
          <button
            type="button"
            onClick={() => onChange("")}
            className="w-7 h-7 shrink-0 rounded-lg text-ink-400 hover:bg-rose-50 hover:text-rose-600 flex items-center justify-center"
            title="Remove"
          >
            <X size={13} />
          </button>
        </div>
      )}
    </div>
  );
};

export default ImageInput;
